import { makeAutoObservable, runInAction } from 'mobx'

import type {
    DashboardPerformance,
    DashboardPeriod,
    DashboardSummary,
    DispatcherOption,
} from '@/Ship/Contracts/DashboardSummary'
import { LoadDashboardPerformanceTask, LoadDashboardSummaryTask } from '@/Ship/Tasks/LoadDashboardSummaryTask'
import { LoadDispatchersTask } from '@/Ship/Tasks/LoadDispatchersTask'

const errorText = (e: any, fallback: string): string => {
    const status = e?.statusCode ?? e?.response?.status
    if (status === 403) return 'Немає доступу до цих даних'
    return e?.message || fallback
}

/**
 * Стор головної: лічильники (воронки, «Потребує уваги», затримки), показники за період
 * з динамікою, один фільтр «Відповідальний диспетчер» на всю сторінку.
 *
 * Помилки зберігаються в полях *Error і показуються в блоці (без тостів: головна
 * оновлюється щохвилини, тост на кожну невдалу спробу заважав би). Відповідь запиту,
 * що встиг застаріти (змінили фільтр чи період), відкидається за лічильником запитів.
 *
 * Екземпляр на монтування сторінки: `useState(() => new DashboardStore())`.
 */
export class DashboardStore {
    summary: DashboardSummary | null = null
    summaryLoading = true
    summaryError: string | null = null

    performance: DashboardPerformance | null = null
    performanceLoading = true
    performanceError: string | null = null

    period: DashboardPeriod = 'week'
    /** Вибраний диспетчер (null — усі) */
    dispatcher: DispatcherOption | null = null
    /** Коли дані востаннє успішно оновлено */
    updatedAt: Date | null = null
    /** Для якого диспетчера завантажено summary (щоб не показати старі числа під новим підписом) */
    summaryDispatcherId: number | null = null
    /** Для якого періоду й диспетчера завантажено показники */
    performanceKey: string | null = null

    dispatcherOptions: DispatcherOption[] = []
    dispatchersLoading = false

    private dispatchersLoadedOnce = false
    private summarySeq = 0
    private performanceSeq = 0

    constructor() {
        makeAutoObservable<this, 'dispatchersLoadedOnce' | 'summarySeq' | 'performanceSeq'>(this, {
            dispatchersLoadedOnce: false,
            summarySeq: false,
            performanceSeq: false,
        })
    }

    /** Звичайний диспетчер — бачить лише свої дані, фільтра диспетчера немає */
    get isScopedDispatcher(): boolean {
        return this.summary?.scope === 'dispatcher'
    }

    /** summary відповідає вибраному диспетчеру */
    get summaryFresh(): boolean {
        return this.summary !== null && this.summaryDispatcherId === (this.dispatcher?.value ?? null)
    }

    /** Показники відповідають вибраним періоду й диспетчеру */
    get performanceFresh(): boolean {
        return this.performance !== null && this.performanceKey === this.currentPerformanceKey
    }

    /**
     * Динаміка за 30 днів від періоду не залежить: після зміни періоду вона лишається на
     * екрані, поки їдуть нові показники; після зміни диспетчера — ні
     */
    get trendFresh(): boolean {
        return this.performance !== null && this.performanceKey?.split(':')[1] === String(this.dispatcher?.value ?? '')
    }

    private get currentPerformanceKey(): string {
        return `${this.period}:${this.dispatcher?.value ?? ''}`
    }

    get refreshing(): boolean {
        return this.summaryLoading || this.performanceLoading
    }

    setDispatcher(option: DispatcherOption | null): void {
        this.dispatcher = option
        void this.refresh()
    }

    setPeriod(period: DashboardPeriod): void {
        if (this.period === period) return
        this.period = period
        void this.fetchPerformance()
    }

    /** Оновити всі блоки */
    async refresh(): Promise<void> {
        await Promise.all([this.fetchSummary(), this.fetchPerformance()])
    }

    async fetchSummary(): Promise<void> {
        const seq = ++this.summarySeq
        const dispatcherId = this.dispatcher?.value ?? null
        this.summaryLoading = true
        try {
            const data = await LoadDashboardSummaryTask(dispatcherId)
            if (seq !== this.summarySeq) return
            runInAction(() => {
                this.summary = data
                this.summaryDispatcherId = dispatcherId
                this.summaryError = null
                this.updatedAt = new Date()
            })
            // Опції фільтра — лише тим, хто його бачить, і один раз
            if (!this.dispatchersLoadedOnce && !this.isScopedDispatcher) {
                this.dispatchersLoadedOnce = true
                void this.fetchDispatchers()
            }
        } catch (e: any) {
            if (seq !== this.summarySeq) return
            runInAction(() => {
                this.summaryError = errorText(e, 'Не вдалося завантажити дані головної')
            })
        } finally {
            if (seq === this.summarySeq) {
                runInAction(() => {
                    this.summaryLoading = false
                })
            }
        }
    }

    async fetchPerformance(): Promise<void> {
        const seq = ++this.performanceSeq
        const key = this.currentPerformanceKey
        this.performanceLoading = true
        try {
            const data = await LoadDashboardPerformanceTask(this.period, this.dispatcher?.value)
            if (seq !== this.performanceSeq) return
            runInAction(() => {
                this.performance = data
                this.performanceKey = key
                this.performanceError = null
            })
        } catch (e: any) {
            if (seq !== this.performanceSeq) return
            runInAction(() => {
                this.performanceError = errorText(e, 'Не вдалося завантажити показники')
            })
        } finally {
            if (seq === this.performanceSeq) {
                runInAction(() => {
                    this.performanceLoading = false
                })
            }
        }
    }

    /** Опції фільтра диспетчера; помилка — порожній список */
    async fetchDispatchers(search?: string): Promise<void> {
        this.dispatchersLoading = true
        try {
            const options = await LoadDispatchersTask(search)
            runInAction(() => {
                this.dispatcherOptions = options
            })
        } catch {
            // список просто лишиться порожнім
        } finally {
            runInAction(() => {
                this.dispatchersLoading = false
            })
        }
    }
}
